import React, { FunctionComponent } from 'react'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { Button, Cell, Text, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { useRoutePageName } from '../../config/router'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useAppRouter, useCheckoutState, useGiftCardDetails, useGiftCardPromoBreakdown, useIsCheckoutLoadingOrUpdating } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { GiftCardPromoBreakdown } from '../GiftCardPromoBreakdown'
import { ImageWithFallback } from '../ImageWithFallback'
import { MerchantAvatarFallback } from '../MerchantAvatarFallback'
import { Money } from '../Money'
import { OrderSummaryPromo } from '../OrderSummary'
import { TotalAmountPayable } from '../TotalAmountPayable'
import styles from './style.module.css'

export const GIFT_CARD_SUMMARY_CARD_NAMESPACE = 'gift-card-summary-card'

const TILE_WIDTH = 45
const TILE_HEIGHT = 30
const INITIALS_LENGTH = 2

const getInitials = (value: string): string =>
  value
    .split(/\s+/)
    .filter((word) => word !== '')
    .slice(0, INITIALS_LENGTH)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')

interface GiftCardSummaryCardProps {
  showPromotion?: boolean
}

export const GiftCardSummaryCard: FunctionComponent<GiftCardSummaryCardProps> = ({ showPromotion = false }) => {
  const { t } = useTranslation(['common', 'summary'])
  const { locale } = useAppRouter()
  const { checkout, started } = useCheckoutState()
  const pageName = useRoutePageName()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { name, imageUrl, faceValue } = useGiftCardDetails()
  const promotion = useGiftCardPromoBreakdown()
  const isCheckoutLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()
  const hasAfterpayPromotion = valueIsNotNullOrUndefined(started?.promotion)
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const initials = getInitials(name ?? t('summary:giftCard.card.title', { context: 'default' }))

  const handleEdit = (): void => {
    logEvent(`Pressed Edit Gift Card on ${pageName} Page`)
    postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_GIFT_CARD_CLOSE)
  }

  const asset = (
    <div className={styles.image} aria-hidden='true'>
      <div className={styles.tile}>
        {imageUrl !== null ? (
          <ImageWithFallback
            src={imageUrl}
            alt=''
            width={TILE_WIDTH}
            height={TILE_HEIGHT}
            unoptimized
            className={styles.imageContent}
            fallback={<MerchantAvatarFallback placeholderText={initials} />}
          />
        ) : (
          <MerchantAvatarFallback placeholderText={initials} />
        )}
      </div>
    </div>
  )

  const body = (
    <Text size='XS' color='Gray40' renderAs='span' testNameSpace={`${GIFT_CARD_SUMMARY_CARD_NAMESPACE}-subtitle`}>
      {name}
      {name !== null && faceValue !== null && (
        <>
          {' '}
          <span aria-hidden='true'>·</span>{' '}
        </>
      )}
      {faceValue !== null && (
        <span className={styles.amount}>
          <Money locale={locale} value={faceValue} size='XS' color='Gray40' testNameSpace={GIFT_CARD_SUMMARY_CARD_NAMESPACE} />
        </span>
      )}
    </Text>
  )

  const accessory = (
    <Button
      type='button'
      kind='Secondary'
      size='Small'
      aria-label={t('summary:giftCard.card.editLabel', { context: 'default' })}
      testNameSpace={`${GIFT_CARD_SUMMARY_CARD_NAMESPACE}-edit`}
      onClick={handleEdit}
    >
      {t('common:edit')}
    </Button>
  )

  return (
    <>
      <div className={designLanguageClass.card} data-testid={GIFT_CARD_SUMMARY_CARD_NAMESPACE}>
        <Cell
          asset={asset}
          label={t('summary:giftCard.card.title', { context: 'default' })}
          body={body}
          accessory={accessory}
          accessoryPosition='Center'
          testNameSpace={GIFT_CARD_SUMMARY_CARD_NAMESPACE}
        />
      </div>
      {showPromotion && (
        <div className={styles.promotion}>
          {hasAfterpayPromotion && <OrderSummaryPromo orderAmount={started?.consumerAmount ?? checkout?.amount} showAmount={false} />}
          {promotion && <GiftCardPromoBreakdown promotion={promotion} loading={isCheckoutLoadingOrUpdating} />}
          <TotalAmountPayable orderBreakdownIsHidden={promotion === null} />
        </div>
      )}
    </>
  )
}
